import type { AmbientLifeConfig, ThemeAssetEntry } from '@goodtime/engine';
import birdDownUrl from '../assets/ambient/bird_down.png';
import birdUpUrl from '../assets/ambient/bird_up.png';

/**
 * The background's creatures, painted (assets/ambient), as the slot scene
 * looks them up. They load with the rest of the theme after the first
 * spin, so a slot opens no slower.
 */
export const AMBIENT_ART: Record<string, ThemeAssetEntry> = {
  ambient_bird_up: { alias: 'ambient:bird_up', src: birdUpUrl, path: 'ambient/bird_up.png' },
  ambient_bird_down: { alias: 'ambient:bird_down', src: birdDownUrl, path: 'ambient/bird_down.png' },
};

/** Light tints: sunlight through leaves, lamplight, desert sun, snow, sea. */
const SUN = 0xfff0c2;
const LAMP = 0xffd88a;
const DESERT = 0xffe0a6;
const SNOW = 0xe8f4ff;
const SEA = 0xb8f0ff;
const CANDY = 0xfff0f4;

/** Out of doors in daylight: birds and dappled sun. */
const meadow = (birdsEverySec: number, beams = 1): AmbientLifeConfig => ({
  birdsEverySec,
  light: { tint: SUN, patches: 5, beams },
});
/** Indoors: only the light moves. */
const indoors = (tint = LAMP, beams = 2): AmbientLifeConfig => ({ light: { tint, patches: 4, beams } });

/**
 * What lives in each slot's background (producer 2026-10-14: birds
 * flitting, dappled light shifting), by theme id. Each suits its painting:
 * gardens, meadows and deserts get birds, rooms and caves only their light,
 * under the sea light from above. Night cities, space and lava have
 * nothing. A theme not listed has nothing. No slot has butterflies: they
 * drew the eye from the reels (producer 2026-10-19).
 */
export const AMBIENT_LIFE: Record<string, AmbientLifeConfig> = {
  // Out of doors, in sun.
  forest: meadow(14, 2),
  gardens: meadow(16),
  knights: meadow(12),
  sherbet_square: { birdsEverySec: 18, light: { tint: CANDY, patches: 5, beams: 1 } },
  lollipop_lane: { light: { tint: CANDY, patches: 5, beams: 1 } },
  fizz_factory: { light: { tint: CANDY, patches: 4 } },
  peppermint_pipeworks: { light: { tint: CANDY, patches: 4 } },
  caravan: { birdsEverySec: 14, light: { tint: DESERT, patches: 4, beams: 1 } },
  hot_springs: { birdsEverySec: 16, light: { tint: SUN, patches: 4, beams: 1 } },
  // Deserts and dusty towns: birds overhead, hot light.
  frontier: { birdsEverySec: 12, light: { tint: DESERT, patches: 4, beams: 1 } },
  stampede: { birdsEverySec: 12, light: { tint: DESERT, patches: 4, beams: 1 } },
  high_noon: { birdsEverySec: 14, light: { tint: DESERT, patches: 4 } },
  // Snow in daylight: birds and cold light.
  avalanche: { birdsEverySec: 20, light: { tint: SNOW, patches: 3, beams: 1 } },
  frost: { birdsEverySec: 22, light: { tint: SNOW, patches: 3, beams: 1 } },
  // Halls, temples, rooms and caves: only the light moves.
  royal: indoors(LAMP, 2),
  cleopatra: indoors(),
  eye_of_ra: indoors(),
  pharaohs_riches: indoors(),
  pharaoh_tomb: indoors(LAMP, 1),
  anubis: indoors(LAMP, 1),
  book_of_dead: indoors(LAMP, 1),
  saloon: indoors(),
  bounty: indoors(),
  wizard: indoors(LAMP, 1),
  sugarplum_vault: indoors(LAMP, 1),
  cocoa_river: indoors(LAMP, 1),
  gummy_lab: indoors(CANDY, 1),
  map_fragment: indoors(LAMP, 1),
  gold_mine: indoors(LAMP, 0),
  dungeons: indoors(LAMP, 0),
  buried_treasure: indoors(LAMP, 2),
  glacier_keep: indoors(SNOW, 1),
  // Under the sea: light from above.
  underwater: { light: { tint: SEA, patches: 4, beams: 3 } },
  mermaid_cove: { light: { tint: SEA, patches: 4, beams: 3 } },
  kraken: { light: { tint: SEA, patches: 3, beams: 2 } },
};
